import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from './contextoAuth';
import { guardarTrabajo, leerTrabajo, sirveLaCopia } from './cacheTrabajo';

// Cargar una ruta de la API de forma que la pantalla siga sirviendo sin
// señal — y que NUNCA mienta sobre de cuándo es lo que enseña.
//
// Es el envoltorio de `api.get` para las pantallas desde las que se
// trabaja en el almacén. Hace tres cosas y las tres importan:
//
//   1. Si la API contesta, se pinta eso y se guarda copia.
//   2. Si no contesta y hay copia, se pinta la copia Y SE DEVUELVE
//      `momento`, la hora a la que se trajo. La pantalla está obligada a
//      enseñarla: por eso viene en el mismo objeto y no en un rincón.
//   3. Si no contesta y no hay copia, se dice eso — no se deja una
//      pantalla vacía que parece que no hay nada que hacer.
//
// POR QUÉ NO SE CACHEA TODO. Solo pasa por aquí lo que se necesita para
// EMPEZAR un trabajo sin cobertura: la hoja de conteo y las órdenes por
// recibir. Un informe de márgenes viejo no sirve para nada y una caché
// que lo guarda todo es una caché que nadie sabe qué contiene.
export function useDatosDeTrabajo(ruta) {
  const { usuario } = useAuth();
  const idUsuario = usuario?.id;
  // La ruta va DENTRO del estado, y de ahí sale «cargando»: al cambiar de
  // filtro hay que dejar de enseñar lo anterior en el acto —una lista con
  // el título de otra es de las mentiras más fáciles de colar— y así se
  // sabe sin tener que poner una bandera desde el efecto.
  const [estado, setEstado] = useState({
    ruta: null, datos: null, error: null, momento: null,
  });

  const traer = useCallback(async (vivo = () => true) => {
    try {
      const datos = await api.get(ruta);
      if (!vivo()) return;
      setEstado({ ruta, datos, error: null, momento: null });
      // Después de pintar: guardar copia no puede retrasar lo que ya se
      // puede enseñar, y si falla no cambia nada de lo que ve el usuario.
      guardarTrabajo(idUsuario, ruta, datos);
    } catch (e) {
      // La regla vive en `cacheTrabajo` y tiene nombre: la copia solo tapa
      // lo que es falta de red, nunca un 401 ni un 403.
      const esDeRed = sirveLaCopia(e);
      const copia = esDeRed ? await leerTrabajo(idUsuario, ruta) : null;
      if (!vivo()) return;
      if (copia) {
        setEstado({
          ruta, datos: copia.datos, error: null, momento: copia.momento,
        });
      } else {
        // Sin copia, el mensaje distingue los dos casos: «no hay señal y
        // no traes copia» pide una acción distinta que «el servidor dice
        // que no». Un «error al cargar» a secas no dice ninguna de las dos.
        setEstado({
          ruta,
          datos: null,
          momento: null,
          error: (navigator.onLine || !esDeRed)
            ? e.message
            : 'Sin conexión, y este dato no está guardado en el teléfono. '
              + 'Ábrelo una vez con señal para poder consultarlo después.',
        });
      }
    }
  }, [ruta, idUsuario]);

  useEffect(() => {
    let vivo = true;
    // Se entra por la promesa y no llamando a `traer()` en el cuerpo del
    // efecto: aunque el `setState` de dentro ocurra siempre después de un
    // await, la regla no puede saberlo y avisa. Es el mismo rodeo, y por
    // el mismo motivo, que en `useConexion`.
    Promise.resolve().then(() => traer(() => vivo));
    return () => { vivo = false; };
  }, [traer]);

  const recargar = useCallback(() => traer(), [traer]);

  // Mientras lo que hay en estado sea de otra ruta, no es de esta: ni se
  // pinta ni se dice que hubo un error de esta.
  const alDia = estado.ruta === ruta;
  return {
    datos: alDia ? estado.datos : null,
    error: alDia ? estado.error : null,
    momento: alDia ? estado.momento : null,
    cargando: !alDia,
    deCache: alDia && estado.momento !== null,
    recargar,
  };
}
